import React, { useCallback, useEffect, useState } from 'react';
import { Stack, Typography, Box, Button } from '@mui/material';
import { Add } from '@mui/icons-material';

import { isScored, roundMax } from '../../../utils/admissionAssessment';
import SectionCard from './SectionCard';
import { TableCard } from '../tabLayout';
import { ListRow, QuietNote, numSx } from './sectionParts';
import InterviewRoundEditor from './InterviewRoundEditor';
import { RUBRIC_KIND_LABEL } from './RubricItemRow';
import { assignKeys } from '../../../utils/fieldKey';
import { AssessmentPathway, InterviewRoundWithRubric, InterviewRubricItem } from '../../../types/admissions';

interface Props {
  pathway: AssessmentPathway;
  canEdit: boolean;
  /** Why the section can't change on this open cycle (marks / offers in). */
  lockedReason?: string | null;
  onSave: (patch: Partial<AssessmentPathway>) => Promise<void>;
}

/**
 * InterviewConfigSection — interview rounds and the questions on each
 * round's sheet. One round = one dialog on the Candidate drawer's
 * Assessment tab. Scored questions add up to the round's total; choice and
 * note questions are recorded with it, unscored.
 */

/** A question worth keeping: named, and complete for its kind. */
const keepItem = (c: InterviewRubricItem): boolean => {
  if (!c.criterion.trim()) return false;
  if (isScored(c)) return c.maxMarks > 0;
  return c.kind !== 'choice' || (c.options || []).length > 0;
};

/** The right-hand side of a question in the read view. */
const itemSummary = (c: InterviewRubricItem): string => {
  if (isScored(c)) return String(c.maxMarks);
  const what = c.kind === 'choice' ? (c.options || []).join(' / ') : RUBRIC_KIND_LABEL.note;
  return c.required ? `${what} · required` : what;
};


const InterviewConfigSection: React.FC<Props> = ({ pathway, canEdit, lockedReason, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<InterviewRoundWithRubric[]>([]);

  useEffect(() => {
    if (editing) setDraft(pathway.interviewConfig?.rounds || []);
  }, [editing, pathway.interviewConfig?.rounds]);

  const stageEnabled = pathway.stages?.interviewEnabled;
  const effective = editing ? draft : (pathway.interviewConfig?.rounds || []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        interviewConfig: {
          rounds: assignKeys(draft.filter(r => r.name.trim()), r => r.name, 'round', 40)
            .map(r => {
              const rubric = (r.rubric || [])
                .filter(keepItem)
                .map(c => ({ ...c, criterion: c.criterion.trim() }));
              // A round with scored questions is out of their total.
              return { ...r, rubric, markStep: r.markStep === 0.5 ? 0.5 : 1, maxMarks: roundMax({ ...r, rubric }) };
            }),
        },
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const addRound = () => setDraft(d => [...d, {
    key: '',
    name: '',
    maxMarks: 50,
    markStep: 1,
    rubric: [],
  }]);

  const updateRound = useCallback((idx: number, patch: Partial<InterviewRoundWithRubric>) =>
    setDraft(d => d.map((r, i) => (i === idx ? { ...r, ...patch } : r))), []);

  const removeRound = useCallback((idx: number) =>
    setDraft(d => d.filter((_, i) => i !== idx)), []);

  // The copy gets a fresh key on save, and its own name to change.
  const duplicateRound = useCallback((idx: number) =>
    setDraft(d => [
      ...d.slice(0, idx + 1),
      { ...d[idx], key: '', name: d[idx].name.trim() ? `${d[idx].name.trim()} (copy)` : '', rubric: (d[idx].rubric || []).map(c => ({ ...c })) },
      ...d.slice(idx + 1),
    ]), []);

  return (
    <SectionCard
      title="Interviews & observations"
      description="Each round and the questions on its sheet — scored, a choice, or a note."
      canEdit={canEdit}
      lockedReason={lockedReason}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft(pathway.interviewConfig?.rounds || [])}
      saving={saving}
      headerActions={
        editing && (
          <Button size="small" startIcon={<Add fontSize="small" />} onClick={addRound}>
            Add round
          </Button>
        )
      }
    >
      <Stack spacing={2}>
        {!stageEnabled && !editing && (
          <QuietNote>Interviews and observations are off. Turn them on under Stages.</QuietNote>
        )}

        {effective.length === 0 ? (
          <QuietNote>No interview rounds yet — add a round and the criteria it is marked on.</QuietNote>
        ) : !editing ? (
          <TableCard>
            {effective.map((r, idx) => (
              <ListRow key={r.key || idx} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="baseline" spacing={1.5}>
                    <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0 }}>{r.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={numSx}>
                      {[r.kind === 'observation' ? 'Observation' : null, `${roundMax(r)} marks`, r.markStep === 0.5 ? 'half marks' : null].filter(Boolean).join(' · ')}
                    </Typography>
                  </Stack>
                  {(r.rubric || []).length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 1, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                      {(r.rubric || []).map((rb, j) => (
                        <Stack key={j} direction="row" alignItems="baseline" spacing={1.5}>
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>{rb.criterion}</Typography>
                          <Typography variant="caption" color={isScored(rb) ? 'text.secondary' : 'text.disabled'} sx={{ ...numSx, textAlign: 'right' }}>
                            {itemSummary(rb)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </ListRow>
            ))}
          </TableCard>
        ) : effective.map((r, idx) => (
          <InterviewRoundEditor key={idx} round={r} index={idx} onChange={updateRound} onRemove={removeRound} onDuplicate={duplicateRound} />
        ))}
      </Stack>
    </SectionCard>
  );
};

export default InterviewConfigSection;
